import WizardCard from "../../components/WizardCard"
import { InputLabel, WrappedInput } from "../../components/Input"
import SelectInput from "../../components/Input/SelectInput"
import CaptionList from "../../components/CaptionList"
import TextPair from "./TextPair"
import { formatCurrency } from "../../utils"

export interface LiabilityInsuranceFinalizeFormProps {
  legalEntityNameLabel: string
  acreage: number
  generalPremium: number
  totalPremium: number
  coveragePeriodOptions: { value: string; label: string }[]
  isMember: boolean
}

const LiabilityInsuranceFinalizeForm = ({
  legalEntityNameLabel,
  acreage,
  generalPremium,
  totalPremium,
  coveragePeriodOptions,
  isMember,
}: LiabilityInsuranceFinalizeFormProps) => (
  <div
    className="container w-[752px] max-w-full flex flex-col gap-6 px-5 lg:px-10 mt-10"
    data-testid="liability-insurance-finalize-form"
  >
    <WizardCard paddingSize="sm">
      <div className="flex flex-col gap-4">
        <h2
          className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
          data-testid="your-information-heading"
        >
          Your Information
        </h2>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Full name"
            type="text"
            name="full_name"
            id="full_name"
            disabled
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Email"
            type="text"
            name="email"
            id="email"
            as="email"
            disabled
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Primary Property Address"
            type="text"
            name="property_address_line_1"
            id="property_address_line_1"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Secondary Property Address (optional)"
            type="text"
            name="property_address_line_2"
            id="property_address_line_2"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Property City"
            type="text"
            name="property_city"
            id="property_city"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Property State"
            type="text"
            name="property_state"
            id="property_state"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Property Zip Code"
            type="text"
            name="property_zip_code"
            id="property_zip_code"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label="Phone number"
            type="text"
            name="phone_number"
            id="phone_number"
          />
        </div>

        <div>
          <WrappedInput
            labelClass="mb-1"
            label={legalEntityNameLabel}
            aria-label={legalEntityNameLabel}
            type="text"
            name="legal_entity_name"
            id="legal_entity_name"
          />
        </div>
      </div>
    </WizardCard>

    <WizardCard paddingSize="sm">
      <div className="flex flex-col gap-4">
        <h2
          className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
          data-testid="electronic-signature-heading"
        >
          Electronic Signature
        </h2>

        <div>
          <WrappedInput
            label="Digital Signature*"
            labelClass="mb-1"
            placeholder="Type your full name as your electronic signature"
            type="text"
            name="electronic_signature"
            id="electronic_signature"
            instructions="By typing your name above, you agree to electronically sign this document."
          />
        </div>

        <div>
          <WrappedInput
            label="Date"
            labelClass="mb-1"
            type="date"
            name="electronic_signature_date"
            id="electronic_signature_date"
          />
        </div>

        <div>
          <InputLabel className="mb-1 inline" htmlFor="coverage_period">
            Coverage Period
          </InputLabel>

          <SelectInput
            options={coveragePeriodOptions}
            placeholder="Select coverage period"
            name="coverage_period"
            id="coverage_period"
          />
        </div>

        <div className="bg-cloud-300 border border-dusk-100 rounded p-6">
          <CaptionList
            caption="By signing above, you acknowledge that:"
            list={[
              "You have reviewed and agree to the policy terms and conditions",
              "All information provided is accurate and complete",
              "You authorize payment for the insurance coverage",
              "You understand the policy is fully earned at inception",
            ]}
            itemClass="text-sm !tracking-[0.28px] before:!top-0 -ml-2 mt-3"
          />
        </div>
      </div>
    </WizardCard>

    <WizardCard paddingSize="sm">
      <div className="flex flex-col gap-6">
        <h2
          className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
          data-testid="order-summary-heading"
        >
          Order Summary
        </h2>

        <div>
          <h3
            className="text-base font-body font-bold leading-[140%] tracking-[0.32px] mb-3"
            data-testid="selected-properties-heading"
          >
            Selected Properties
          </h3>

          <TextPair
            leftText="Property"
            rightText={`${acreage} acres`}
            testId="property-acreage"
          />

          <hr className="border-cloud-500 my-3" />

          <p
            className="text-dusk-700 text-base font-body leading-[140%] tracking-[0.32px]"
            data-testid="total-acreage"
          >
            Total: {acreage} acres
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <h3
            className="text-base font-body font-bold leading-[140%] tracking-[0.32px]"
            data-testid="coverage-heading"
          >
            Coverage
          </h3>

          <TextPair
            leftText="General Timberland Liability"
            rightText={formatCurrency(generalPremium, true)}
            testId="general-liability-premium"
          />

          {!isMember ? (
            <TextPair
              leftText="NCX Membership Fee"
              rightText="$12/billed monthly"
              testId="membership-fee"
            />
          ) : null}
        </div>

        <hr className="border-cloud-500 my-3" />

        <div className="flex flex-col gap-3">
          <TextPair
            leftText="DUE TODAY:"
            rightText={formatCurrency(totalPremium, true)}
            leftClass="text-red-600 text-xl font-bold"
            rightClass="text-xl font-bold"
            testId="total-premium-due"
          />

          <p
            className="text-dusk-700 text-base font-body leading-[140%] tracking-[0.32px]"
            data-testid="coverage-starts"
          >
            Coverage begins immediately upon payment confirmation
          </p>
        </div>
      </div>
    </WizardCard>
  </div>
)

export default LiabilityInsuranceFinalizeForm
